import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface IvrAssignedNumberShort {
  id: number
  number: string
  friendly_name: string
}

export interface IvrFlowSummary {
  id: number
  name: string
  description: string | null
  is_active: boolean
  node_count: number
  assigned_numbers_count: number
  assigned_numbers?: IvrAssignedNumberShort[]
  total_calls: number
  created_at: string
  updated_at: string
}

export interface IvrAssignedNumber {
  id: number
  phone_number: string
  is_toll_free: boolean
}

export interface IvrNode {
  id?: number
  node_id: string
  node_type: string
  label: string
  position_x: number
  position_y: number
  config: Record<string, unknown>
}

/**
 * Format renvoyé par le backend (IVREdgeSerializer) :
 *   { id, edge_id, source_node, target_node, source_node_id, target_node_id,
 *     source_handle, label, created_at }
 * Le format SAVE attendu par le backend (IVRFlowSaveSerializer) :
 *   { edge_id, source, target, sourceHandle, label }
 *
 * On expose les deux formats pour s'adapter à la lecture et à l'écriture.
 */
export interface IvrEdge {
  id?: number
  edge_id: string
  /** Backend response only — `node_id` de la source. */
  source_node_id?: string
  /** Backend response only — `node_id` de la cible. */
  target_node_id?: string
  /** Backend response only — handle source (snake_case). */
  source_handle?: string
  /** SAVE payload only — node_id source (camelCase pour ReactFlow). */
  source?: string
  /** SAVE payload only — node_id cible (camelCase pour ReactFlow). */
  target?: string
  /** SAVE payload only — handle source (camelCase pour ReactFlow). */
  sourceHandle?: string
  label: string
}

export interface IvrFlowDetail extends IvrFlowSummary {
  nodes: IvrNode[]
  edges: IvrEdge[]
}

export interface IvrPhoneNumberShort {
  id: number
  number: string
  friendly_name: string
  assignment_type: string
  ivr_flow: number | null
}

export interface IvrAvailableResources {
  teams: Array<{ id: number; name: string }>
  agents: Array<{ id: number; name: string }>
  ai_agents: Array<{ id: number; name: string }>
  schedules: Array<{ id: number; name: string }>
  phone_lists: Array<{ id: number; name: string }>
  phone_numbers: IvrPhoneNumberShort[]
}

export interface AssignNumberInput {
  phone_number_id: number
}

export interface CreateIvrFlowInput {
  name: string
  description?: string
}

export interface UpdateIvrFlowInput {
  name?: string
  description?: string
  is_active?: boolean
  nodes?: Array<Omit<IvrNode, 'id'>>
  edges?: Array<Omit<IvrEdge, 'id'>>
}

/** GET /api/ivr-flows/ — liste sommaire des flows IVR. */
export function useIvrFlows() {
  return useQuery({
    queryKey: ['ivr-flows'],
    queryFn: async () => {
      const { data } = await api.get<IvrFlowSummary[]>('/api/ivr-flows/')
      return data
    },
    staleTime: 30_000,
  })
}

/** GET /api/ivr-flows/{id}/ — détail complet (nodes + edges). */
export function useIvrFlowDetail(flowId: number | null) {
  return useQuery({
    queryKey: ['ivr-flow', flowId],
    queryFn: async () => {
      const { data } = await api.get<IvrFlowDetail>(`/api/ivr-flows/${flowId}/`)
      return data
    },
    enabled: !!flowId,
  })
}

/** GET /api/ivr-flows/available-resources/ — teams, agents, AI agents, schedules, phone lists, phone numbers. */
export function useIvrAvailableResources(enabled = true) {
  return useQuery({
    queryKey: ['ivr-available-resources'],
    queryFn: async () => {
      const { data } = await api.get<IvrAvailableResources>(
        '/api/ivr-flows/available-resources/',
      )
      return data
    },
    enabled,
    staleTime: 30_000,
  })
}

/** POST /api/ivr-flows/ — crée un nouveau flow.
 *  Tout flow doit avoir un nœud "start" : on l'injecte automatiquement à la
 *  création pour que l'éditeur soit utilisable d'emblée (sinon canvas vide,
 *  pas de point d'entrée valide pour exécuter le flow).
 */
export function useCreateIvrFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateIvrFlowInput) => {
      const payload = {
        ...input,
        nodes: [
          {
            node_id: `start-${Date.now()}`,
            node_type: 'start',
            label: 'Début',
            position_x: 250,
            position_y: 150,
            config: {},
          },
        ],
      }
      const { data } = await api.post<IvrFlowSummary>('/api/ivr-flows/', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
    },
  })
}

/** PUT /api/ivr-flows/{id}/ — sauvegarde complète d'un flow (avec nodes + edges). */
export function useUpdateIvrFlow(flowId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateIvrFlowInput) => {
      const { data } = await api.put<IvrFlowDetail>(
        `/api/ivr-flows/${flowId}/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
      queryClient.invalidateQueries({ queryKey: ['ivr-flow', flowId] })
      // La page du numéro affiche le nom du flux et son nombre de nœuds.
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
    },
  })
}

/** PATCH /api/ivr-flows/{id}/ — patch partiel (rename, toggle active). */
export function usePatchIvrFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      flowId,
      input,
    }: {
      flowId: number
      input: Partial<UpdateIvrFlowInput>
    }) => {
      const { data } = await api.patch<IvrFlowSummary>(
        `/api/ivr-flows/${flowId}/`,
        input,
      )
      return data
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
      queryClient.invalidateQueries({ queryKey: ['ivr-flow', vars.flowId] })
    },
  })
}

/** DELETE /api/ivr-flows/{id}/ — supprime un flow. */
export function useDeleteIvrFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (flowId: number) => {
      await api.delete(`/api/ivr-flows/${flowId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
    },
  })
}

/** POST /api/ivr-flows/{id}/duplicate/ — duplique un flow. */
export function useDuplicateIvrFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (flowId: number) => {
      const { data } = await api.post<IvrFlowSummary>(
        `/api/ivr-flows/${flowId}/duplicate/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
    },
  })
}

/** POST /api/ivr-flows/{flowId}/assign-number/ — assigne un numéro à un flow. */
export function useAssignNumberToFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      flowId,
      phoneNumberId,
    }: {
      flowId: number
      phoneNumberId: number
    }) => {
      const { data } = await api.post(
        `/api/ivr-flows/${flowId}/assign-number/`,
        { phone_number_id: phoneNumberId },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
    },
  })
}

/** POST /api/ivr-flows/{flowId}/unassign-number/ — retire un numéro d'un flow. */
export function useUnassignNumberFromFlow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      flowId,
      phoneNumberId,
    }: {
      flowId: number
      phoneNumberId: number
    }) => {
      const { data } = await api.post(
        `/api/ivr-flows/${flowId}/unassign-number/`,
        { phone_number_id: phoneNumberId },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-phone-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['ivr-flows'] })
    },
  })
}
